import { BLUE, CYAN, GREEN, MAGENTA, RED, YELLOW } from '../util/colors';

export interface Subsystem {
  // Not having an http port happens for subsystems
  // that do not have http lambdas like mxcoder or tools
  port?: number;

  wsPort?: number;
  shortName?: string;
  envVarName?: string;
  defaultLocal?: boolean;
  routes?: Record<string, string>;
  nameInUrl?: boolean;

  // Terminal escape sequence used to colorize some outputs
  color: string;
}

export const roots = {
  core: '/api/v1',
  prime: '/prime/api/v1',
  config: '/config/api/v1',
  auxiliary: '/auxiliary/api/v1',
  auth: '/auth/api/v1',
  global: '/global/api/v1',
};

/**
 * The name of the file listing all the Kelda packages.
 */
export const KELDA_PACKAGE_LIST = 'packages.json';


export const primeRoutes = <const>{
  recordLog: `${roots.prime}/recordLog`,

  /**
   * See common/src/api/replicationCommands.ts
   */
  replication: `${roots.prime}/replication`,

  keldaAdmin: `${roots.prime}/kelda/admin`,
  keldaWorkerConfig: `${roots.prime}/kelda/workerConfig/{id}`,
  keldaSupervisorConfig: `${roots.prime}/kelda/supervisorConfig/{id}`,
  packages: `${roots.prime}/packages/{id}`,
  mos: `${roots.prime}/mos/{id}`,
  nextMosMessage: `${roots.prime}/mos/{id}/{queue}`,
  liveItems: `${roots.prime}/live/items`,
  liveItem: `${roots.prime}/live/items/{id}`,
  liveSourcesV2: `${roots.prime}/live/sourcesV2`,
  liveSourceV2: `${roots.prime}/live/sourcesV2/{id}`,
  livePipelines: `${roots.prime}/live/pipelines`,
  keldaMediaPipelineStatus: `${roots.prime}/live/pipeline/status/{liveSourceId}/{pipelineId}`,
  opsGlance: `${roots.prime}/ops/glance`,
  scheduledRecordings: `${roots.prime}/live/scheduled`,
  scheduledRecording: `${roots.prime}/live/scheduled/{id}`,
  recordingSeriesCollection: `${roots.prime}/live/series`,
  recordingSeries: `${roots.prime}/live/series/{id}`,
  recordingSeriesItems: `${roots.prime}/live/series/{id}/items`,
  liveLocations: `${roots.prime}/liveLocations`,
  liveLocation: `${roots.prime}/liveLocation/{id}`,
  allRouterSources: `${roots.prime}/allroutersources`,
  allRouterControllerStatuses: `${roots.prime}/allroutercontrollerstatuses`,
  qc: `${roots.prime}/qc/{id}`,
  routers: `${roots.prime}/routers`,
  router: `${roots.prime}/routers/{id}`,
  routerSources: `${roots.prime}/routers/{id}/sources`,
  routerCrosspoints: `${roots.prime}/routers/{id}/crosspoints`,
  routerMessages: `${roots.prime}/routers/{id}/messages`,
  ndiRouter: `${roots.prime}/ndi/router/{liveSourceId}/{pipelineId}`,
  mediaPipelineRouter: `${roots.prime}/live/router/{liveSourceId}/{pipelineId}`,
  ndiSources: `${roots.prime}/ndi/sources`,
  dynamicSourceList: `${roots.prime}/live/dynamicSourceList/{sourceListKey}`,
  deduplication: `${roots.prime}/deduplication`,
  itemEditStatus: `${roots.prime}/itemEditStatus/{id}`,
  keldaS3ServerKeys: `${roots.prime}/keldaS3ServerKeys/{keldaId}`,
  externalShares: `${roots.prime}/shares`,
  externalShare: `${roots.prime}/shares/{id}`,
  playoutCommands: `${roots.prime}/playout/commands`,
  playoutDevices: `${roots.prime}/playout/devices`,
  playoutStatuses: `${roots.prime}/playout/statuses`,
  playableItem: `${roots.prime}/playableItem/{itemId}`,
  urlArray: `${roots.prime}/urlArray`,
  peaks: `${roots.prime}/peaks/{itemId}/track/{audioTrackId}`,
  peaksDelta: `${roots.prime}/peaks/{itemId}/delta/{peakOffset}`,
  itemPolling: `${roots.prime}/itemPolling`,
  shareToken: `${roots.prime}/shareToken`,
  shareData: `${roots.prime}/shareData`,
  shareContent: `${roots.prime}/shareContent`,
  shareMediaSegments: `${roots.prime}/shareMedia/{itemId}/{streamIds}/{signedForMethod}/{firstSegmentIndex}`,
  shareRecordLog: `${roots.prime}/shareRecordLog`,
  shareItemUpload: `${roots.prime}/shareUpload/{itemId}`,
  shareUploadProgress: `${roots.prime}/shareUploadProgress`,
  shareIngestFileFromExternalS3: `${roots.prime}/shareIngestFileFromExternalS3/{itemId}`,
  shareFolderUpload: `${roots.prime}/shareFolderUpload/{folderId}`,
  shareDownload: `${roots.prime}/shareDownload/{itemId}`,
  shareItemMetadata: `${roots.prime}/shareMetadata/{itemId}`,
  shareMdf: `${roots.prime}/shareSchema/{id}`,
  shareMdfs: `${roots.prime}/shareSchemas/{id}`,
  shareOptionList: `${roots.prime}/shareOptionLists/{id}`,
  sharePlayableItem: `${roots.prime}/sharePlayableItem`,
  sharePeaks: `${roots.prime}/sharePeaks/{itemId}/track/{audioTrackId}`,
  shareItemPolling: `${roots.prime}/shareItemPolling`,
  sharePeaksDelta: `${roots.prime}/sharePeaks/{itemId}/delta/{peakOffset}`,
  shareUrlArray: `${roots.prime}/shareUrlArray`,
  storages: `${roots.prime}/storages`,
  systemTaskList: `${roots.prime}/systemTasks`,
  tamsConfig: `${roots.prime}/tams/{id}`,
  tamsConfigs: `${roots.prime}/tams`,
  tamsToken: `${roots.prime}/tams/{id}/token`,
  housekeepingRun: `${roots.prime}/internal/housekeeping`,
  housekeepingState: `${roots.prime}/internal/housekeeping/{taskId}`,
  storageTransfers: `${roots.prime}/transfers`,
  getIPAddress: `${roots.prime}/getIPAddress`,
  vttIds: `${roots.prime}/vtt/{itemId}/ids`,
  vttCues: `${roots.prime}/vtt/{itemId}/cues/{vttType}/{vttId}`,

  /**
   * Create Job Multipart Upload
   *
   * Request:
   *     Method: POST
   *     Body: CreateJobMultipartUploadInput
   * Response:
   *     Body: CreateJobMultipartUploadOutput
   */
  jobUploadMultipart: `${roots.prime}/job/upload/multipart`,

  /**
   * Get Job Multipart Upload URLs
   *
   * Request:
   *     Method: GET
   *     Path parameters:
   *         kind: 'generated' | 'highres'
   *         firstPartNumber: number // 1..n
   *     Query parameters:
   *         keySuffix: string | undefined
   * Response:
   *     Body: GetJobMultipartUploadUrlsOutput
   */
  jobUploadMultipartUrls: `${roots.prime}/job/upload/multipart/{kind}/{uploadId}/urls/{firstPartNumber}`,

  /**
   * Complete Job Multipart Upload
   *
   * Request:
   *     Method: POST
   *     Path parameters:
   *         kind: 'generated' | 'highres'
   *     Query parameters:
   *         keySuffix: string | undefined
   *     Body: CompleteJobMultipartUploadInput
   *
   */
  jobUploadMultipartCompletion: `${roots.prime}/job/upload/multipart/{kind}/{uploadId}`,

  /**
   * Sign Job Upload URLs
   *
   * Request:
   *     Method: POST
   *     Body: SignJobUploadUrlsInput
   *
   * Response:
   *     Body: SignJobUploadUrlsOutput
   */
  jobUploadUrls: `${roots.prime}/job/upload/urls`,

  batchJobUploadUrls: `${roots.prime}/job/upload/batchUrls`,

  /**
   * Conclude a job upload operation. Example: update item with references to uploaded content
   *
   * Request:
   *    Method: POST
   *    Body: ConcludeJobUploadInput
   */
  concludeJobUpload: `${roots.prime}/job/upload/conclude`,

  batchConcludeJobUpload: `${roots.prime}/job/upload/batchConclude`,

  /**
   * The shareJobUpload endpoints follow the API of the jobUpload endpoints.
   */
  shareJobUploadUrls: `${roots.prime}/shareJobUpload/urls`,
  shareJobUploadMultipart: `${roots.prime}/shareJobUpload/multipart`,
  shareJobUploadMultipartCompletion: `${roots.prime}/shareJobUpload/multipart/{kind}/{uploadId}`,
  shareJobUploadMultipartCompletionV2: `${roots.prime}/shareJobUpload/multipartV2/{kind}/{uploadId}`,
  shareJobUploadMultipartUrls: `${roots.prime}/shareJobUpload/multipart/{kind}/{uploadId}/urls/{firstPartNumber}`,

  awsMp: `${roots.prime}/awsMp`,
  mxcoderEvents: `${roots.prime}/comms/events`,

  /**
   * These end points are used by the sequence renderer when running in mxcoder
   */
  mxcoderRenderSequenceParams: `${roots.prime}/comms/renderSequenceParams`,
  mxcoderOperationParams: `${roots.prime}/comms/operationParams`,
  mxcoderItem: `${roots.prime}/comms/items/{itemId}`,
  mxcoderTargetItem: `${roots.prime}/comms/targetItem`,
  mxcoderMediaSegments: `${roots.prime}/comms/items/{itemId}/mediaSegments/{streamIds}/{signedForMethod}/{firstSegmentIndex}`,
  mxcoderUpdateProgress: `${roots.prime}/comms/items/{itemId}/progress`,
  mxcoderItemAttachment: `${roots.prime}/comms/items/{itemId}/attachments`,

  renderSequenceRequests: `${roots.prime}/renderSequenceRequests`,
  itemRender: `${roots.prime}/items/{itemId}/render`,

  encodingPresets: `${roots.prime}/presets`,
  encodingPreset: `${roots.prime}/presets/{id}`,
  externalAwsAccountConfigs: `${roots.prime}/externalAwsAccountConfigs`,
  externalAwsAccountConfig: `${roots.prime}/externalAwsAccountConfigs/{accountId}/{capability}`,

  feedItems: `${roots.prime}/feed/items/{since}`,

  /**
   * See GetItemBundleCommand in common/src/api/bundleCommands.ts
   */
  itemBundle: `${roots.prime}/items/{itemId}/bundle`,

  cookies: `${roots.prime}/cookies`,

  userTask: `${roots.prime}/tasks/{assignee}/{taskId}`,
  userTasks: `${roots.prime}/tasks/{assignee}`,

  notifications: `${roots.prime}/notifications`,
  notification: `${roots.prime}/notifications/{id}`,
  markNotification: `${roots.prime}/notifications/{id}/mark`,
  catchupNotifications: `${roots.prime}/notifications/catchup`,

  websocketInfo: `${roots.prime}/websocketInfo/{keldaId}/{keldaInstanceId}`,

  cropImage: `${roots.prime}/cropImage`,

  // We rely on CloudWatch to map the service root over to the prime API
  // Gateway.
  serviceRoot: `${roots.core}`,
  ssoDomain: `${roots.prime}/ssoDomain`,
  ssoConfig: `${roots.prime}/ssoConfig`,

  timeline: `${roots.prime}/timelines/{itemId}`,

};

export const configApiRoutes = <const>{
  // note: The convention for the config API has been to put
  // tenant config related endpoints under ${roots.config}/config/
  changelogs: `${roots.config}/changelogs`,
  featureFlags: `${roots.config}/featureFlags`,
  featureFlag: `${roots.config}/featureFlags/{id}`,
  bookmarks: `${roots.config}/bookmarks`,
  bookmark: `${roots.config}/bookmarks/{id}`,
  bookmarkFolders: `${roots.config}/bookmarkFolders`,
  customActions: `${roots.config}/config/customActions`,
  customAction: `${roots.config}/config/customActions/{id}`,
  webhooks: `${roots.config}/config/webhooks`,
  webhook: `${roots.config}/config/webhooks/{id}`,
  customIcons: `${roots.config}/config/customIcons`,
  customIcon: `${roots.config}/config/customIcons/{id}`,
  plugins: `${roots.config}/config/plugins`,
  plugin: `${roots.config}/config/plugins/{id}`,
};

export const coreRoutes = <const>{
  aaf: `${roots.core}/items/{itemId}/aaf`,
  folderPath: `${roots.core}/folders/path`,
  folderMetadata: `${roots.core}/folders/{folderId}/metadata`,
  folderSafe: `${roots.core}/folders/{folderId}/safe`,
  mergeItems: `${roots.core}/items/merge`,
  archiveTransfer: `${roots.core}/items/{itemId}/archiveTransfer`,
  markTransfer: `${roots.core}/storages/{storageId}/transferRequests/mark/{transferRequestId}`,
  thumbnailStrip: `${roots.core}/items/{itemId}/thumbnailStrip`,
  thumbnailUrlInTime: `${roots.core}/items/{itemId}/thumbnail/{inPoint}`,
  thumbnailUrls: `${roots.core}/items/{itemId}/thumbnails/urls`,
  thumbnailUploadUrls: `${roots.core}/items/{itemId}/thumbnails/upload-urls`,
  itemMediaUsage: `${roots.core}/items/{itemId}/mediaUsage`,
  pendingOperationsSummary: `${roots.core}/pendingOperationsSummary`,
  deleteHighres: `${roots.core}/items/{itemId}/deleteHighres`,
  clipList: `${roots.core}/clipLists/{itemId}`,
  itemMetadata: `${roots.core}/itemMetadata/{itemId}`,
  batchItems: `${roots.core}/batchItems`,
  itemsByExternalId: `${roots.core}/itemsByExternalId`,
  migrateMediaLocation: `${roots.core}/items/{itemId}/migrateMediaLocation`,
  /**
   * Cancel an ongoing tasks or operations performed on the item.
   * e.g: ongoing render sequences, kelda requests, or pending operations etc.
   */
  cancelTask: `${roots.core}/items/{itemId}/cancelTask`,
  mdfToEXIFMapping: `${roots.core}/mdfToEXIFMapping`,
  getEXIFEmbeddedImageURL: `${roots.core}/getEXIFEmbeddedImageURL`,
  warnings: `${roots.core}/items/{itemId}/warnings`,

  generateDerivedMedia: `${roots.core}/items/{itemId}/generateDerivedMedia`,
};

export const auxRoutes = <const>{
  tenantSystemActivity: `${roots.auxiliary}/systemActivity`,
  itemSystemActivity: `${roots.auxiliary}/systemActivity/item/{itemId}`,
  keldaRequests: `${roots.auxiliary}/keldaRequests`,
  keldaIngestFile: `${roots.auxiliary}/keldaIngestFile/{keldaId}`,
};

export const authRoutes = <const>{
  userInfo: `${roots.auth}/userInfo/users`,
  avatar: `${roots.auth}/userInfo/users/avatar`,
  security: `${roots.auth}/security`,
  apiKeys: `${roots.auth}/keys`,
  apiKey: `${roots.auth}/keys/{keyId}`,
};

export const globalRoutes = <const>{
  stages: `${roots.global}/stages`,
  globalConfig: `${roots.global}/globalConfig`,
};

export const subsystemData = {
  vuetify: {
    port: 8080,
    envVarName: 'MIMIR_HOME_URL',
    defaultLocal: true,
    color: CYAN,
  },
  docs: {
    port: 5173,
    envVarName: 'MIMIR_DOCS_URL',
    defaultLocal: true,
    color: GREEN,
  },
  core: {
    port: 3000,
    defaultLocal: true,
    routes: coreRoutes,
    color: BLUE,
  },
  auth: { port: 3001, routes: authRoutes, color: RED },
  auxiliary: { port: 3002, routes: auxRoutes, shortName: 'aux', color: GREEN },
  ingest: { port: 3003, color: MAGENTA },
  'config-api': { port: 3004, shortName: 'config', routes: configApiRoutes, color: YELLOW },
  prime: { port: 3006, wsPort: 3008, routes: primeRoutes, envVarName: 'VUE_APP_AWS_API_PREFIX', color: RED },
  mxcoder: { port: undefined, color: CYAN },
  tools: { port: undefined, color: CYAN },
  // TODO: Run global subsystem locally
  global: { port: undefined, routes: globalRoutes, color: RED },
};

export type SubsystemId = keyof typeof subsystemData;
export const subsystems: Record<SubsystemId, Subsystem> = subsystemData;

export const routes = <const>{ ...primeRoutes, ...coreRoutes, ...configApiRoutes, ...authRoutes, ...auxRoutes };

/**
 * Derive service link data type from declared deployment routes.
 *
 * Add routes to the patterns property of the routes common module to
 * automatically extend this type with new links.
 */
export type ServiceLinksBase = {
  [name in keyof typeof routes]: string;
};

